import { useEffect, useState } from 'react';
import { api, type CodePartsResult, type Vehicle } from '../lib/api.ts';

interface Props {
  code: string | null;
  vehicle: Vehicle | null;
}

export function PartsPanel({ code, vehicle }: Props) {
  const [data, setData] = useState<CodePartsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .partsForCode(code, vehicle ? { ...vehicle, vin: vehicle.vin } : undefined)
      .then((res) => !cancelled && setData(res))
      .catch((err) => !cancelled && setError((err as Error).message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [code, vehicle?.vin]);

  if (!code) return null;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="row wrap" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, flex: 1 }}>
          Parts for <span className="mono">{code}</span>
        </h2>
        {data && <span className={`pill ${data.mode === 'live' ? 'ok' : 'warn'}`}>{data.mode} catalog</span>}
      </div>

      {loading && <p className="dim small">Looking up parts…</p>}
      {error && <div className="banner error small">{error}</div>}

      {data?.notice && <div className="banner info small" style={{ marginBottom: 12 }}>{data.notice}</div>}

      {data && data.candidates.length === 0 && (
        <div className="empty">
          <strong>No mapping for this code yet</strong>
          {data.system} fault. Add an entry to the DTC-to-parts table in the bridge to make this code resolvable.
        </div>
      )}

      {data?.candidates.map((candidate) => (
        <article className="part-candidate" key={candidate.category}>
          <header>
            <strong style={{ flex: 1 }}>{candidate.category}</strong>
            <span className={`pill ${confidenceTone(candidate.confidence)}`}>{candidate.confidence} likelihood</span>
          </header>

          {candidate.recalls && candidate.recalls.length > 0 && (
            <div className="banner warn small" style={{ borderRadius: 0, borderWidth: '0 0 1px 0' }}>
              <strong>Covered by an open recall.</strong> {candidate.recalls.map((r) => r.campaignNumber).join(', ')} —{' '}
              {candidate.recalls[0].component}. The manufacturer repairs this free of charge, so check with a dealer
              before buying anything below.
            </div>
          )}

          {candidate.note && <p className="note" style={{ margin: 0 }}>{candidate.note}</p>}

          {candidate.lookupState === 'needs-part-number' ? (
            <p className="small dim" style={{ padding: '12px 14px', margin: 0 }}>
              Worldpac prices part numbers rather than categories, so this one needs an application catalog lookup to
              produce a number first.
            </p>
          ) : candidate.offers.length === 0 ? (
            <p className="small dim" style={{ padding: '12px 14px', margin: 0 }}>
              No offers returned.
            </p>
          ) : (
            <div className="scroll-x">
              <table className="offers">
                <thead>
                  <tr>
                    <th>Part</th>
                    <th>Brand</th>
                    <th className="n">List</th>
                    <th className="n">Your price</th>
                    <th>Availability</th>
                  </tr>
                </thead>
                <tbody>
                  {candidate.offers.map((offer) => {
                    const local = offer.availability.find((a) => !/national/i.test(a.location));
                    return (
                      <tr key={offer.partNumber}>
                        <td className="mono">{offer.partNumber}</td>
                        <td>{offer.brand ?? '—'}</td>
                        <td className="n dim">{money(offer.listPrice, offer.currency)}</td>
                        <td className="n">
                          <strong>{money(offer.yourPrice, offer.currency)}</strong>
                        </td>
                        <td className="small">
                          {local ? (
                            <span className="stock-in">
                              {local.quantity} at {local.location}
                            </span>
                          ) : (
                            <span className="stock-dc">DC only</span>
                          )}
                          <br />
                          <span className="dim">{offer.totalQuantity} total</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

function confidenceTone(c: 'high' | 'medium' | 'low'): string {
  return { high: 'crit', medium: 'warn', low: '' }[c];
}

function money(value: number | null, currency: string): string {
  if (value === null) return '—';
  return value.toLocaleString(undefined, { style: 'currency', currency: currency || 'USD' });
}
